import React, { useState, useEffect, useRef } from "react";
import { useAgencyData } from "@/hooks/useAgencyData";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

const AgencyWhitelabel: React.FC = () => {
  const { agency, loading, updateAgency } = useAgencyData();
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !supabase || !agency) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato não suportado. Use PNG, JPG, SVG ou WebP.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 2MB.');
      return;
    }

    setUploadingLogo(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `agency-${agency.id}-${Date.now()}.${fileExt}`;
      const filePath = `logos/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('agency-logos')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        if (uploadError.message?.includes('Bucket not found')) {
          toast.error('Bucket de storage não configurado. Contate o administrador.');
          return;
        }
        throw uploadError;
      }

      const { data: urlData } = supabase.storage
        .from('agency-logos')
        .getPublicUrl(filePath);

      if (urlData?.publicUrl) {
        setFormData(prev => ({ ...prev, logo_url: urlData.publicUrl }));
        toast.success('Logo carregada com sucesso!');
      }
    } catch (err: any) {
      console.error('Erro ao fazer upload:', err);
      toast.error('Erro ao fazer upload da logo.');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTestSmtp = async () => {
    if (!formData.smtp_host || !formData.smtp_email || !formData.smtp_port || !formData.smtp_password) {
      toast.error('Preencha todos os campos SMTP antes de testar.');
      return;
    }

    setTestingSmtp(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-smtp`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            smtp_host: formData.smtp_host,
            smtp_email: formData.smtp_email,
            smtp_port: parseInt(formData.smtp_port),
            smtp_ssl: formData.smtp_ssl === 'true',
            smtp_password: formData.smtp_password,
            test_email: formData.smtp_email,
          })
        }
      );

      const result = await response.json();
      if (result.success) {
        toast.success('E-mail de teste enviado com sucesso!');
      } else {
        toast.error(result.error || 'Erro ao enviar e-mail de teste.');
      }
    } catch (err) {
      toast.error('Erro ao testar SMTP. Verifique os dados e tente novamente.');
    } finally {
      setTestingSmtp(false);
    }
  };

  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    complement: "",
    city: "",
    state: "",
    domain: "",
    api_url: "",
    smtp_host: "",
    smtp_email: "",
    smtp_port: "",
    smtp_ssl: "true",
    smtp_password: "",
    logo_url: "",
  });

  useEffect(() => {
    if (agency) {
      setFormData({
        company_name: agency.company_name || "",
        contact_name: agency.contact_name || "",
        email: agency.email || "",
        phone: agency.phone || "",
        address: agency.address || "",
        complement: agency.complement || "",
        city: agency.city || "",
        state: agency.state || "",
        domain: agency.domain || "",
        api_url: agency.api_url || "",
        smtp_host: agency.smtp_host || "",
        smtp_email: agency.smtp_email || "",
        smtp_port: agency.smtp_port?.toString() || "",
        smtp_ssl: agency.smtp_ssl ? "true" : "false",
        smtp_password: agency.smtp_password || "",
        logo_url: agency.logo_url || "",
      });
    }
  }, [agency]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const dataToUpdate: any = {
        company_name: formData.company_name,
        contact_name: formData.contact_name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address || null,
        complement: formData.complement || null,
        city: formData.city || null,
        state: formData.state || null,
        domain: formData.domain,
        api_url: formData.api_url,
        smtp_host: formData.smtp_host || null,
        smtp_email: formData.smtp_email || null,
        smtp_port: formData.smtp_port ? parseInt(formData.smtp_port) : null,
        smtp_ssl: formData.smtp_ssl === "true",
        smtp_password: formData.smtp_password || null,
        logo_url: formData.logo_url || null,
        updated_at: new Date().toISOString(),
      };

      const success = await updateAgency(dataToUpdate);

      if (success) {
        setMessage({ type: 'success', text: 'Dados salvos com sucesso!' });
      } else {
        setMessage({ type: 'error', text: 'Erro ao salvar dados' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erro ao salvar dados' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
          <p className="text-neutral-50">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-7xl">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-neutral-50 mb-2">
          Configurações White Label
        </h3>
        <p className="text-neutral-400 text-sm">
          Configure a identidade visual e informações da agência
        </p>
      </div>

      {message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-500/10 border border-green-500/30 text-green-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-neutral-800/50 border border-[#3F3F464D] rounded-lg p-6">
          <h4 className="text-lg font-semibold text-neutral-50 mb-6">
            Configurações
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Label htmlFor="company_name" className="text-white">
                Nome do Empresa
              </Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="Nome"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="contact_name" className="text-white">
                Contacto
              </Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => handleInputChange('contact_name', e.target.value)}
                placeholder="Contacto"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="email" className="text-white">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="E-mail"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="phone" className="text-white">
                Telefone
              </Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Telefone"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                required
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="address" className="text-white">
                Endereço
              </Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder="Endereço"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="complement" className="text-white">
                Complemento
              </Label>
              <Input
                id="complement"
                value={formData.complement}
                onChange={(e) => handleInputChange('complement', e.target.value)}
                placeholder="Complemento"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="city" className="text-white">
                Cidade
              </Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="Cidade"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="state" className="text-white">
                Estado
              </Label>
              <Input
                id="state"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="Estado"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
              />
            </div>
          </div>
        </div>

        <div className="bg-neutral-800/50 border border-[#3F3F464D] rounded-lg p-6">
          <h4 className="text-lg font-semibold text-neutral-50 mb-6">
            White Label
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Label htmlFor="logo_url" className="text-white">
                Logo
              </Label>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <div className="flex gap-3">
                <Button
                  type="button"
                  className="bg-neutral-600 hover:bg-neutral-700 text-white border-none"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingLogo ? 'Enviando...' : 'Upload'}
                </Button>
                <Input
                  id="logo_url"
                  value={formData.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                  placeholder="URL da logo"
                  className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                />
              </div>
              {formData.logo_url && (
                <div className="mt-2">
                  <img
                    src={formData.logo_url}
                    alt="Logo preview"
                    className="max-h-[60px] object-contain rounded"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="domain" className="text-white">
                Domínio
              </Label>
              <Input
                id="domain"
                value={formData.domain}
                onChange={(e) => handleInputChange('domain', e.target.value)}
                placeholder="Domínio"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                required
              />
            </div>

            <div className="flex flex-col gap-3 md:col-span-2">
              <Label htmlFor="api_url" className="text-white">
                API
              </Label>
              <Input
                id="api_url"
                value={formData.api_url}
                onChange={(e) => handleInputChange('api_url', e.target.value)}
                placeholder="API"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
                required
              />
            </div>
          </div>
        </div>

        <div className="bg-neutral-800/50 border border-[#3F3F464D] rounded-lg p-6">
          <h4 className="text-lg font-semibold text-neutral-50 mb-6">SMTP</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_host" className="text-white">
                Host
              </Label>
              <Input
                id="smtp_host"
                value={formData.smtp_host}
                onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                placeholder="Host"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_email" className="text-white">
                E-mail
              </Label>
              <Input
                id="smtp_email"
                type="email"
                value={formData.smtp_email}
                onChange={(e) => handleInputChange('smtp_email', e.target.value)}
                placeholder="E-mail"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_port" className="text-white">
                Porta
              </Label>
              <Input
                id="smtp_port"
                type="number"
                value={formData.smtp_port}
                onChange={(e) => handleInputChange('smtp_port', e.target.value)}
                placeholder="Porta"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
              />
            </div>

            <div className="flex flex-col gap-3">
              <Label htmlFor="smtp_ssl" className="text-white">
                SSL
              </Label>
              <Select
                value={formData.smtp_ssl}
                onValueChange={(value) => handleInputChange('smtp_ssl', value)}
              >
                <SelectTrigger className="rounded-[6px] py-3 text-white border border-neutral-500 bg-neutral-600 px-3">
                  <SelectValue placeholder="SSL" />
                </SelectTrigger>
                <SelectContent className="bg-neutral-600 border-neutral-500">
                  <SelectItem value="true" className="text-white hover:bg-neutral-700">
                    Sim
                  </SelectItem>
                  <SelectItem value="false" className="text-white hover:bg-neutral-700">
                    Não
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-3 md:col-span-2">
              <Label htmlFor="smtp_password" className="text-white">
                Senha
              </Label>
              <Input
                id="smtp_password"
                type="password"
                value={formData.smtp_password}
                onChange={(e) => handleInputChange('smtp_password', e.target.value)}
                placeholder="Senha"
                className="rounded-[6px] py-3 placeholder:text-neutral-400 text-white border border-neutral-500 bg-neutral-600 px-3"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white min-w-[140px]"
            disabled={testingSmtp || !formData.smtp_host || !formData.smtp_email}
            onClick={handleTestSmtp}
          >
            {testingSmtp ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Testando...
              </>
            ) : 'Testar SMTP'}
          </Button>
          <Button
            type="submit"
            className="bg-[#E4E4E7] text-black hover:bg-[#D4D4D8] min-w-[120px]"
            disabled={saving}
          >
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AgencyWhitelabel;
